import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import type { MultipleSelectClient, Role } from '@/company-portal/add-file/add-file.types';
import { ClientContext, getAvailableClients, resetAvailableClients } from '@/company-portal/add-file/available-clients';
import { EDIT_CLIENT_FIELDS, prepareModalForFormClientEditing, setEditedClientId } from '@/company-portal/add-file/form-clients';
import { type ClientMultiSelectName, REQUEST_IDV_NAME } from '@/company-portal/add-file/selectors';
import type { DsMultiSelectOptionBase } from '@/components/design-system/multiselect/multi-select.types';
import type { ClientOption, ClientType } from '@/components/Views/Company/File/file.types';
import { joinFirstLast } from '@/shared/utils/strings';
import { useUserStore } from '@/stores/company/user.store';
import { computed, onUnmounted, ref, watch } from 'vue';

export type DsMultiSelectClientOption = ClientOption & DsMultiSelectOptionBase;

/**
 * Maps a MultipleSelectClient (from getAvailableClients) to the shape expected by DsMultiSelect.
 */
export const mapClientToDsMultiSelectClientOption = (
    name: ClientMultiSelectName,
    client: MultipleSelectClient,
    index: number,
    idvDependentClientTypes: Role[],
): DsMultiSelectClientOption => {
    // In IDV dropdown: clients with IDV dependency are disabled
    const isDisabled = client.role && idvDependentClientTypes.includes(client.role) && name === REQUEST_IDV_NAME;
    // the value expected by the backend for form submission is a form-row index as string ('0', '1', '2', …)
    const formValue = client.value ?? String(index);
    return {
        ...client,
        id: formValue,
        first: client.first ?? '',
        last: client.last ?? '',
        value: formValue,
        label: joinFirstLast(client.first, client.last),
        phone: client.phone ?? '',
        role: client.role as ClientType, // assertion is needed because we have duplicate type declarations for client role -- @todo cleanup in [CLENG-883]
        disabled: isDisabled,
    };
};

export const useClientMultiSelect = (name: ClientMultiSelectName) => {
    const userStore = useUserStore();

    // Selected clients
    const selectedClientsMap = ref(new Map<MultipleSelectClient['id'], DsMultiSelectClientOption>());

    const selectedClientsArray = computed<DsMultiSelectClientOption[]>({
        get: () => Array.from(selectedClientsMap.value.values()),
        set: selectedClientOptions => {
            const newSelectedClientsMap = new Map<MultipleSelectClient['id'], DsMultiSelectClientOption>();
            selectedClientOptions.forEach(client => newSelectedClientsMap.set(client.id, client));
            selectedClientsMap.value = newSelectedClientsMap;
        },
    });

    // Client options
    const clientOptionsMap = computed<Map<MultipleSelectClient['id'], DsMultiSelectClientOption>>(() => {
        const availableClients = getAvailableClients(ClientContext.NEW).map((client, index) =>
            mapClientToDsMultiSelectClientOption(name, client, index, userStore.idvDependentClientTypes),
        );

        return new Map(availableClients.map(client => [client.id, client]));
    });

    const clientOptionsArray = computed<DsMultiSelectClientOption[]>(() => {
        return Array.from(clientOptionsMap.value.values());
    });

    // IDV logic
    // Clients required for IDV are auto-selected so they appear checked and show chips
    /** @todo [CLENG-1151] separate IDV-specific behavior from this composable */
    const idvRequiredClients = computed(() => {
        return clientOptionsArray.value.filter(option => userStore.requireIdvClientTypes.includes(option.role as Role)); // assertion is needed because we have duplicate type declarations for client role -- @todo cleanup in [CLENG-883]
    });

    watch(
        idvRequiredClients,
        (newIdvRequiredClients, oldIdvRequiredClients) => {
            if (name !== REQUEST_IDV_NAME) return;

            const newIdvRequiredMap = new Map(newIdvRequiredClients.map(client => [client.id, client]));
            const oldIdvRequiredMap = new Map((oldIdvRequiredClients ?? []).map(client => [client.id, client]));

            // Remove from selectedClients any client that was required but is no longer
            const newSelectedClientsMap = new Map(
                [...selectedClientsMap.value.entries()].filter(
                    ([key]) => key && (!oldIdvRequiredMap.has(key) || newIdvRequiredMap.has(key)),
                ),
            );
            // Add any newly required clients that are not yet selected
            newIdvRequiredMap.forEach((client, key) => {
                if (!newSelectedClientsMap.has(key)) newSelectedClientsMap.set(key, client);
            });
            selectedClientsMap.value = newSelectedClientsMap;
        },
        { immediate: true },
    );

    // "Select all" is disabled if any option has disabled: true
    const disableSelectAll = computed(() => clientOptionsArray.value.some(option => option.disabled));

    // Edit client modal
    const openEditClientModal = (clientValue: MultipleSelectClient['value']) => {
        if (clientValue === undefined) return;
        setEditedClientId(clientValue);
        prepareModalForFormClientEditing(EDIT_CLIENT_FIELDS, clientValue);
    };

    // Reset state after form submission
    const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(() => {
        selectedClientsMap.value = new Map<MultipleSelectClient['id'], DsMultiSelectClientOption>();
        resetAvailableClients();
    });

    watch(clientOptionsMap, options => {
        const filtered = new Map([...selectedClientsMap.value].filter(([id]) => options.has(id)));
        if (filtered.size !== selectedClientsMap.value.size) {
            selectedClientsMap.value = filtered;
        }
    });

    onUnmounted(unregisterAddFileSubmittedAction);

    return {
        clientOptions: clientOptionsArray,
        disableSelectAll,
        openEditClientModal,
        selectedClients: selectedClientsArray,
    };
};
